<script setup lang="ts">
import { layoutCmsIncludes } from '~/utils/includes/cms'
import { toIncludesQueryParams } from '~/utils/includesQueryParams'
import { stripCmsPage } from '~/utils/cmsPayloadStrip'

const { loadNavigationElements } = useNavigation()
const { apiClient } = useShopwareContext()
const sidenav = useSidenav()

// Shared field allowlist — same fields for both fetches, only the depth differs.
const NAV_INCLUDES = {
    category: [
        'id', 'name', 'type', 'linkType', 'internalLink', 'externalLink',
        'seoUrls', 'children', 'translated', 'customFields', 'breadcrumb',
        'active', 'visible', 'path', 'level', 'parentId', 'childCount',
        'a4g_brand'
    ],
    category_translation: ['name', 'customFields', 'breadcrumb'],
    seo_url: ['seoPathInfo', 'pathInfo'],
    media: ['url', 'alt', 'fileName', 'path', 'translated'],
    media_translation: ['alt']
}

const { data, error } = await useAsyncData('layout', async () => {
    const [navigationData, layoutCms] = await Promise.all([
        // SSR fetch: top-level only (depth 0). Renders the above-the-fold nav bar + SEO
        // top-level links, but keeps the deep category tree out of __NUXT_DATA__.
        loadNavigationElements({ depth: 0, includes: NAV_INCLUDES }),
        apiClient.invoke('getLayoutCMS get /layout-cms', {
            query: toIncludesQueryParams(layoutCmsIncludes),
        })
    ])

    // Drops what includes can't express at the API level (translated/apiAlias duplicates,
    // config's {source,value} wrapper, never-visible sections/blocks) - see cmsPayloadStrip.ts.
    stripCmsPage(layoutCms.data.header)
    stripCmsPage(layoutCms.data.footer)

    return { navigationData, layoutCms }
})

// Deep tree (flyout children + mobile subcategories) — client-only, never enters the SSR
// hydration payload. Deferred (immediate: false): the desktop nav and the mobile drawer call
// the provided trigger on first interaction, so pages where the menu is never opened pay
// nothing for it.
const { data: deepNavigation, execute: executeDeepNavigation, status: deepNavigationStatus } = useAsyncData('mainNavigationDeep',
    () => loadNavigationElements({ depth: 5, includes: NAV_INCLUDES }),
    { server: false, lazy: true, immediate: false }
)

// Idempotent trigger shared by all deep-tree consumers (desktop mega menu, mobile drawer,
// category sidebar). Fetches at most once per page — the first interaction wins.
const deepNavigationRequested = ref(false)
const loadMainNavigation = () => {
    if (deepNavigationRequested.value) return
    deepNavigationRequested.value = true
    return executeDeepNavigation()
}

// Prefer the deep tree once loaded; fall back to the shallow SSR tree for the first paint.
const navigationData = computed(() => deepNavigation.value ?? data.value?.navigationData)
const layoutCms = computed(() => data.value?.layoutCms?.data)
// True until the deep tree is available (also during SSR) — lets consumers render a skeleton
const mainNavigationPending = computed(() => deepNavigationStatus.value === 'idle' || deepNavigationStatus.value === 'pending')


provide('mainNavigation', navigationData)
provide('loadMainNavigation', loadMainNavigation)
provide('mainNavigationPending', mainNavigationPending)

if (error.value) {
    showError(error.value as Error)
}

useRuntimeHook('page:finish', () => {
    sidenav.closeAll()
})

useHead({
    bodyAttrs: {
        class: computed(() => {
            if (sidenav.anyOpen.value) return 'overflow-hidden'

            return ''
        })
    }
})
</script>

<template>
    <div class="default-layout">
        <LayoutSkipLinks />

        <div v-if="layoutCms?.header">
            <StructureSection
                v-for="section in layoutCms?.header?.sections"
                :key="section.id"
                :content="section"
            />
        </div>

        <!-- Banderole popup teleport target -->
        <div id="banderole-target" />

        <LayoutCountryProposal />

        <slot />

        <div v-if="layoutCms?.footer" class="mt-auto">
            <StructureSection
                v-for="section in layoutCms?.footer?.sections"
                :key="section.id"
                :content="section"
            />
        </div>

        <LayoutNotifications />

        <!-- Sidenav components -->
        <SidenavContext />

        <!-- Teleport target for sidenavs -->
        <div id="teleports" />

        <!-- Permanent teleport targets for filters (must exist before components mount) -->
        <div id="productListingFilter" />
        <div id="searchFilterTarget" />

        <!-- Media gallery portal target -->
        <div class="media-gallery-modal relative z-50">
            <div id="productMediaGallery" />
        </div>
    </div>
</template>
